import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Download } from "lucide-react";
import { getReceivablesAging } from "@/lib/accounting/accounting.functions";
import { downloadCSV, fmtDate, fmtMoney, toCSV } from "@/lib/accounting/exports";

export const Route = createFileRoute("/_authenticated/admin/accounting/receivables")({
  component: AR,
});

function AR() {
  const fn = useServerFn(getReceivablesAging);
  const { data, isLoading } = useQuery({ queryKey: ["accounting", "aging"], queryFn: () => fn() });
  if (isLoading || !data) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;

  const exportCsv = () => {
    const rows = data.rows.map((r: any) => ({
      customer: r.customer_name, invoice: r.invoice_number, due_date: r.due_date,
      days_overdue: r.days_overdue, amount_due: r.balance_due,
    }));
    downloadCSV(`ar_aging_${new Date().toISOString().slice(0, 10)}.csv`, toCSV(rows));
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Bucket label="Current" value={data.buckets.current} />
        <Bucket label="1–30 days" value={data.buckets.d1_30} />
        <Bucket label="31–60 days" value={data.buckets.d31_60} tone="amber" />
        <Bucket label="61–90 days" value={data.buckets.d61_90} tone="red" />
        <Bucket label="90+ days" value={data.buckets.d90} tone="red" />
      </div>
      <div className="flex justify-end">
        <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-200 hover:bg-white/5"><Download className="size-3.5" /> CSV</button>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
            <tr><th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Invoice</th><th className="text-left px-4 py-3">Due date</th><th className="text-right px-4 py-3">Days overdue</th><th className="text-right px-4 py-3">Amount due</th></tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-12 text-emerald-300">All caught up — no outstanding invoices.</td></tr>
            ) : data.rows.map((r: any) => (
              <tr key={r.id} className="border-b border-white/5">
                <td className="px-4 py-3 text-white">{r.customer_name}</td>
                <td className="px-4 py-3"><Link to="/admin/accounting/invoices/$id" params={{ id: r.id }} className="text-amber-300 font-mono text-xs">{r.invoice_number}</Link></td>
                <td className="px-4 py-3 text-zinc-300 text-xs">{fmtDate(r.due_date)}</td>
                <td className={`px-4 py-3 text-right ${r.days_overdue > 60 ? "text-red-300" : r.days_overdue > 30 ? "text-amber-300" : "text-zinc-300"}`}>{r.days_overdue}</td>
                <td className="px-4 py-3 text-right text-white">{fmtMoney(r.balance_due)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Bucket({ label, value, tone }: { label: string; value: number; tone?: "amber" | "red" }) {
  const c = tone === "red" ? "text-red-300" : tone === "amber" ? "text-amber-300" : "text-white";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
      <p className={`mt-1.5 font-serif text-xl ${c}`}>{fmtMoney(value)}</p>
    </div>
  );
}